"use client";

import React from "react";
import { ArrowRight, BadgeCheck, Banknote, MapPin, Clock, Star, PhoneCall } from "lucide-react";

/* ─────────────────────────────────────────
   DATA
───────────────────────────────────────── */

const PROCESS_STEPS = [
  {
    step: "01",
    title: "Mang đàn đến",
    desc: "Ghé trực tiếp tại Guitar Sài Gòn – Quận 12, TPHCM",
    icon: MapPin,
  },
  {
    step: "02",
    title: "Kiểm tra & định giá",
    desc: "Kỹ thuật viên kiểm tra trực tiếp, định giá minh bạch ngay tại chỗ",
    icon: BadgeCheck,
  },
  {
    step: "03",
    title: "Thanh toán nhanh",
    desc: "Đồng ý giá – nhận tiền ngay, không chờ đợi",
    icon: Banknote,
  },
];

const BENEFITS = [
  "Định giá minh bạch, không ép giá",
  "Kiểm tra và báo giá ngay tại chỗ",
  "Thanh toán tiền mặt hoặc chuyển khoản",
  "Thu mua mọi thương hiệu guitar acoustic & classic",
  "Hỗ trợ trao đổi lên đời đàn mới",
];

const STATS = [
  { value: "500+", label: "Cây đã thu mua" },
  { value: "24h",  label: "Định giá nhanh" },
  { value: "100%", label: "Minh bạch" },
];

/* ─────────────────────────────────────────
   COMPONENT
───────────────────────────────────────── */

const BuyBackGuitarSection: React.FC = () => {
  return (
    <section
      aria-labelledby="buyback-heading"
      className="w-full py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8"
      style={{
        backgroundColor: "var(--background)",
        color: "var(--foreground)",
        fontFamily: "var(--font-roboto)",
      }}
    >
      <div className="max-w-7xl mx-auto">

        {/* ══════════════════════════════════════
            HEADER
        ══════════════════════════════════════ */}
        <header className="text-center mb-10 sm:mb-14 lg:mb-16">

          {/* Eyebrow badge */}
          <div
            className="inline-flex items-center gap-1.5 sm:gap-2 px-3.5 sm:px-5 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-6 sm:mb-8 border"
            style={{
              backgroundColor: "rgba(200,169,110,0.08)",
              borderColor: "rgba(200,169,110,0.35)",
              color: "var(--brand-primary)",
              fontFamily: "var(--font-montserrat)",
            }}
          >
            <Banknote className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            Thu Mua Guitar Cũ – TPHCM
          </div>

          {/* ──────────────────────────────────
              3-TIER HEADING
              Tier 1 → descriptor (muted, spaced)
              Tier 2 → hero split (dark + gold)
              Tier 3 → location (muted, light)
          ────────────────────────────────── */}
          <h2
            id="buyback-heading"
            className="mb-5 sm:mb-7 px-1"
            style={{ fontFamily: "var(--font-montserrat)", wordBreak: "keep-all" }}
          >

            {/* Tier 1 — descriptor */}
            <span
              className="block text-xs sm:text-sm font-medium tracking-[0.22em] uppercase mb-2 sm:mb-3"
              style={{ color: "var(--muted-foreground)" }}
            >
              Thu Mua &amp; Trao Đổi
            </span>

            {/* Tier 2 — hero: "Guitar" foreground / "Cũ" gold */}
            <span className="block leading-[1.05] tracking-tight mb-2.5 sm:mb-3">
              <span
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black"
                style={{ color: "var(--foreground)" }}
              >
                Guitar{" "}
              </span>
              <span
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black"
                style={{ color: "var(--brand-primary)" }}
              >
                Cũ
              </span>
            </span>

            {/* Tier 3 — location */}
            <span
              className="block text-sm sm:text-base md:text-lg font-normal tracking-wide"
              style={{ color: "var(--muted-foreground)" }}
            >
              tại{" "}
              <span className="font-semibold" style={{ color: "var(--foreground)" }}>
                TPHCM
              </span>
            </span>
          </h2>

          {/* Gold divider */}
          <div className="flex items-center justify-center gap-2 sm:gap-3 mb-5 sm:mb-7">
            <div className="h-px w-10 sm:w-16 rounded-full" style={{ backgroundColor: "rgba(200,169,110,0.2)" }} />
            <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "var(--brand-primary)" }} />
            <div className="h-px w-10 sm:w-16 rounded-full" style={{ backgroundColor: "rgba(200,169,110,0.2)" }} />
          </div>

          {/* Body copy */}
          <p
            className="text-sm sm:text-base lg:text-lg leading-relaxed max-w-xs sm:max-w-xl md:max-w-2xl mx-auto"
            style={{ color: "var(--muted-foreground)" }}
          >
            Nếu bạn muốn{" "}
            <strong style={{ color: "var(--foreground)", fontWeight: 600 }}>
              bán lại guitar đã qua sử dụng
            </strong>
            , Guitar Sài Gòn hỗ trợ{" "}
            <strong style={{ color: "var(--foreground)", fontWeight: 600 }}>
              thu mua guitar cũ tại Quận 12
            </strong>{" "}
            và các khu vực lân cận. Định giá minh bạch, kiểm tra trực tiếp, thanh toán nhanh.
          </p>
        </header>

        {/* ══════════════════════════════════════
            STATS ROW
        ══════════════════════════════════════ */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-8 sm:mb-12 max-w-xs sm:max-w-sm md:max-w-lg mx-auto">
          {STATS.map(({ value, label }) => (
            <div
              key={label}
              className="flex flex-col items-center gap-0.5 sm:gap-1 py-3.5 sm:py-5 px-2 sm:px-3 rounded-xl sm:rounded-2xl border"
              style={{
                backgroundColor: "var(--card)",
                borderColor: "rgba(200,169,110,0.18)",
                boxShadow: "0 1px 6px rgba(0,0,0,0.1)",
              }}
            >
              <span
                className="text-xl sm:text-2xl font-black leading-none"
                style={{ fontFamily: "var(--font-montserrat)", color: "var(--brand-primary)" }}
              >
                {value}
              </span>
              <span
                className="text-[9px] sm:text-xs font-medium text-center leading-tight mt-0.5"
                style={{ color: "var(--muted-foreground)" }}
              >
                {label}
              </span>
            </div>
          ))}
        </div>

        {/* ══════════════════════════════════════
            MAIN CARD
        ══════════════════════════════════════ */}
        <div
          className="rounded-2xl sm:rounded-3xl overflow-hidden border"
          style={{
            backgroundColor: "var(--card)",
            borderColor: "var(--border)",
            boxShadow: "0 4px 32px rgba(0,0,0,0.15), 0 0 0 1px rgba(200,169,110,0.07)",
          }}
        >
          {/*
            Mobile:  flex-col — image stacked top
            lg+:     2-col grid
          */}
          <div className="flex flex-col lg:grid lg:grid-cols-2">

            {/* ── IMAGE PANEL ── */}
            {/*
              Mobile:  h-52 (208px) — shows image without eating too much viewport
              sm:      h-72 (288px)
              lg+:     auto fill grid row, min-h 460px
            */}
            <div className="relative h-52 sm:h-72 lg:h-auto lg:min-h-[460px] overflow-hidden flex-shrink-0">
              <img
                src="https://images.unsplash.com/photo-1525201548942-d8732f6617a0?w=800&h=600&fit=crop"
                alt="Thu mua guitar cũ tại Guitar Sài Gòn"
                className="w-full h-full object-cover"
                loading="lazy"
                style={{ filter: "brightness(0.85) contrast(1.05)" }}
              />

              {/* Gradient overlay */}
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.1) 48%, transparent 70%)",
                }}
              />

              {/* Gold top bar */}
              <div
                className="absolute top-0 left-0 right-0 h-1"
                style={{ backgroundColor: "var(--brand-primary)" }}
              />

              {/* Floating badge */}
              <div
                className="absolute top-3 sm:top-5 left-3 sm:left-5 flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold border backdrop-blur-sm"
                style={{
                  backgroundColor: "rgba(13,13,13,0.72)",
                  borderColor: "rgba(200,169,110,0.4)",
                  color: "var(--brand-primary)",
                  fontFamily: "var(--font-montserrat)",
                }}
              >
                <MapPin className="w-3 h-3 sm:w-4 sm:h-4" />
                Quận 12 – TPHCM
              </div>

              {/* Bottom label */}
              <div className="absolute bottom-3 sm:bottom-5 left-3 sm:left-5 right-3 sm:right-5">
                <div className="flex items-center gap-0.5 sm:gap-1 mb-1">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-3 h-3 sm:w-4 sm:h-4 fill-current"
                      style={{ color: "var(--brand-primary)" }}
                    />
                  ))}
                </div>
                <p className="text-white text-xs sm:text-sm font-medium leading-snug">
                  "Mang đàn đến – nhận tiền ngay, không chờ đợi"
                </p>
              </div>
            </div>

            {/* ── CONTENT PANEL ── */}
            {/*
              Mobile:  p-5 — comfortable, not cramped
              sm:      p-8
              lg+:     p-10 / p-12
            */}
            <div className="p-5 sm:p-8 lg:p-10 xl:p-12 flex flex-col justify-center">

              {/* Section label */}
              <p
                className="text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-4 sm:mb-5"
                style={{ color: "var(--brand-primary)", fontFamily: "var(--font-montserrat)" }}
              >
                Quy trình 3 bước đơn giản
              </p>

              {/* Process steps */}
              <ol className="space-y-4 sm:space-y-5 mb-6 sm:mb-9">
                {PROCESS_STEPS.map(({ step, title, desc, icon: Icon }, i) => (
                  <li key={i} className="flex items-start gap-3 sm:gap-4">
                    <div
                      className="shrink-0 w-9 h-9 sm:w-10 sm:h-10 lg:w-11 lg:h-11 rounded-xl flex items-center justify-center text-xs sm:text-sm font-black"
                      style={{
                        fontFamily: "var(--font-montserrat)",
                        backgroundColor: "rgba(200,169,110,0.1)",
                        color: "var(--brand-primary)",
                        border: "1.5px solid rgba(200,169,110,0.28)",
                      }}
                    >
                      {step}
                    </div>
                    <div className="flex-1 min-w-0 pt-0.5 sm:pt-1">
                      <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
                        <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" style={{ color: "var(--brand-primary)" }} />
                        <p
                          className="font-black text-sm sm:text-base leading-snug"
                          style={{ fontFamily: "var(--font-montserrat)", color: "var(--foreground)" }}
                        >
                          {title}
                        </p>
                      </div>
                      <p className="text-xs sm:text-sm leading-relaxed" style={{ color: "var(--muted-foreground)" }}>
                        {desc}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>

              {/* Divider */}
              <div className="h-px mb-5 sm:mb-7" style={{ backgroundColor: "var(--border)" }} />

              {/* Benefits */}
              <ul className="space-y-2 sm:space-y-2.5 mb-6 sm:mb-9">
                {BENEFITS.map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5 sm:gap-3">
                    <div
                      className="shrink-0 w-4 h-4 sm:w-5 sm:h-5 mt-0.5 flex items-center justify-center rounded-full text-[9px] sm:text-xs font-black"
                      style={{
                        backgroundColor: "rgba(200,169,110,0.12)",
                        color: "var(--brand-primary)",
                        border: "1.5px solid rgba(200,169,110,0.3)",
                      }}
                    >
                      ✓
                    </div>
                    <span className="text-xs sm:text-sm leading-relaxed" style={{ color: "var(--foreground)" }}>
                      {item}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA — full width on mobile */}
              <a
                href="https://zalo.me/0364669564"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center gap-2 font-black rounded-xl border transition-all duration-150 active:scale-[0.98] w-full sm:w-auto"
                style={{
                  borderColor: "rgba(200,169,110,0.4)",
                  color: "var(--brand-primary)",
                  fontFamily: "var(--font-montserrat)",
                  fontSize: "0.875rem",
                  backgroundColor: "rgba(200,169,110,0.07)",
                  height: "52px",
                  paddingLeft: "1.75rem",
                  paddingRight: "1.75rem",
                  boxShadow: "0 2px 10px rgba(200,169,110,0.1)",
                }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.backgroundColor = "rgba(200,169,110,0.14)";
                  el.style.borderColor = "rgba(200,169,110,0.6)";
                  el.style.boxShadow = "0 4px 16px rgba(200,169,110,0.18)";
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.backgroundColor = "rgba(200,169,110,0.07)";
                  el.style.borderColor = "rgba(200,169,110,0.4)";
                  el.style.boxShadow = "0 2px 10px rgba(200,169,110,0.1)";
                }}
              >
                <PhoneCall className="w-4 h-4" />
                Liên Hệ Thu Đàn
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </a>

              {/* Trust row */}
              <div
                className="mt-5 sm:mt-8 pt-4 sm:pt-6 flex items-center gap-2 sm:gap-3 border-t"
                style={{ borderColor: "var(--border)" }}
              >
                <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" style={{ color: "var(--brand-primary)" }} />
                <p className="text-xs sm:text-sm" style={{ color: "var(--muted-foreground)" }}>
                  Định giá trong{" "}
                  <span className="font-semibold" style={{ color: "var(--foreground)" }}>
                    15–30 phút
                  </span>{" "}
                  — không cần hẹn trước
                </p>
              </div>

            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default BuyBackGuitarSection;